import { useEffect, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, Drawer } from '@jumpstone/ui-react';
import { NAV_ENTRIES, active_sub_tab_id, is_module_active } from './nav_model';
import { navigate } from '../../lib/router';
import type { Route } from '../../lib/router';

/**
 * Narrow-viewport navigation.
 *
 * Uses the design system's Drawer rather than a bespoke slide-over so the
 * focus trap, Escape handling and portal behaviour come for free. Groups
 * expand in place, mirroring the desktop dropdowns.
 */
export function MobileNavDrawer({
  open,
  onClose,
  route,
}: {
  open: boolean;
  onClose: () => void;
  route: Route;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [expanded, set_expanded] = useState<string | null>(null);
  const active_sub_tab = active_sub_tab_id(route);

  // Re-sync which group is expanded to the CURRENT route every time the
  // drawer opens (not just once on mount -- this component stays mounted
  // across navigation, only its `open` prop toggles visibility, so a
  // mount-time-only seed would go stale the moment the user navigates via
  // any OTHER affordance and reopens the drawer). Without this, a route
  // under a section with sub-items opens the drawer fully collapsed, so
  // the item-level `aria-current` highlight this component sets is not
  // visible until the user manually taps the group open -- the exact
  // moment it exists to help.
  useEffect(() => {
    if (!open) return;
    const active_entry = NAV_ENTRIES.find((entry) =>
      is_module_active(route, entry.id),
    );
    set_expanded(active_entry?.sections.length ? active_entry.id : null);
  }, [open, route]);

  const go = (route: Route): void => {
    navigate(route);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('nav.menu')}
      className="wp-mobile-drawer-panel"
      footer={
        <Button variant="secondary" size="sm" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      <nav aria-label={t('nav.primary')} className="flex flex-col">
        {NAV_ENTRIES.map((entry) => {
          const is_expanded = expanded === entry.id;
          const has_children = entry.sections.length > 0;
          const is_group_active = is_module_active(route, entry.id);

          return (
            <div
              key={entry.id}
              className="border-b border-[color:var(--line)] last:border-b-0"
            >
              <button
                type="button"
                className="wp-mobile-group"
                aria-expanded={has_children ? is_expanded : undefined}
                aria-current={is_group_active ? 'page' : undefined}
                onClick={() => {
                  if (has_children) {
                    set_expanded(is_expanded ? null : entry.id);
                  } else {
                    go(entry.route);
                  }
                }}
              >
                <span>{t(entry.label_key)}</span>
                {has_children && (
                  <span aria-hidden="true">{is_expanded ? '▲' : '▼'}</span>
                )}
              </button>

              {has_children && is_expanded && (
                <div className="flex flex-col ps-4 pb-2.5">
                  {entry.sections.map((section, section_index) => (
                    <div
                      key={
                        section.heading_key ??
                        `section-${String(section_index)}`
                      }
                      className="flex flex-col"
                    >
                      {section.heading_key !== undefined && (
                        <span className="wp-menu-heading">
                          {t(section.heading_key)}
                        </span>
                      )}
                      {section.items.map((item) => {
                        const is_item_active = active_sub_tab === item.id;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            className="wp-mobile-item"
                            aria-current={is_item_active ? 'page' : undefined}
                            onClick={() => {
                              go(item.route);
                            }}
                          >
                            {t(item.label_key)}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </Drawer>
  );
}
